import { localUrl as appLocalUrl } from './app-path.js';
const SessionManager = {
	KEYS: {
		SESSION: 'climatempo_session',
		LAST_SUBSCRIPTION_SYNC: 'climatempo_last_subscription_sync',
	},

	getCookie(name) {
		const value = `; ${document.cookie}`;
		const parts = value.split(`; ${name}=`);
		if (parts.length === 2) {
			try {
				return decodeURIComponent(parts.pop().split(';').shift());
			} catch (e) {
				return null;
			}
		}
		return null;
	},

	decodeJWT(token) {
		if (!token) return null;

		try {
			const parts = token.split('.');
			if (parts.length !== 3) return null;

			const payload = parts[1];
			const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
			return JSON.parse(decoded);
		} catch (error) {
			console.error('Erro ao decodificar JWT:', error);
			return null;
		}
	},

	normalizeIdentityValue(value) {
		if (value === null || value === undefined || value === '') return null;
		return String(value).trim().toLowerCase();
	},

	normalizeEmail(value) {
		return this.normalizeIdentityValue(value);
	},

	identitiesMatch(trustedIdentity = {}, candidate = {}) {
		const trustedUserId = this.normalizeIdentityValue(
			trustedIdentity.userId || trustedIdentity.id || trustedIdentity.sub,
		);
		const candidateUserId = this.normalizeIdentityValue(
			candidate.userId || candidate.id || candidate.sub,
		);

		if (trustedUserId && candidateUserId) {
			return trustedUserId === candidateUserId;
		}

		const trustedEmail = this.normalizeEmail(trustedIdentity.email);
		const candidateEmail = this.normalizeEmail(candidate.email);

		if (trustedEmail && candidateEmail) {
			return trustedEmail === candidateEmail;
		}

		return true;
	},

	ensureConsistentSession(serverIdentity = null) {
		const session = this.getSession();
		if (!session?.accessToken) return true;

		const tokenPayload = this.decodeJWT(session.accessToken);
		if (!tokenPayload) {
			return true;
		}

		const tokenIdentity = {
			userId: tokenPayload.userId || tokenPayload.sub,
			email: tokenPayload.email,
		};

		if (session.user && !this.identitiesMatch(tokenIdentity, session.user)) {
			this.clearSession();
			return false;
		}

		if (serverIdentity && !this.identitiesMatch(serverIdentity, tokenIdentity)) {
			this.clearSession();
			return false;
		}

		return true;
	},

	setSession(sessionData) {
		let {
			accessToken,
			refreshToken,
			name,
			userId,
			orderId,
			email,
			image,
			expiresIn,
			subscription,
		} = sessionData;

		let jwtPayload = null;
		if (accessToken) {
			jwtPayload = this.decodeJWT(accessToken);
		}

		const session = {
			user: {
				id: jwtPayload?.userId ?? jwtPayload?.sub ?? userId ?? null,
				orderId: orderId ?? jwtPayload?.orderId ?? null,
				name: name || jwtPayload?.fullName || null,
				email: jwtPayload?.email || email || null,
				image: image || null,
			},
			accessToken: accessToken || null,
			expiresAt: null,
		};

		if (expiresIn) {
			session.expiresAt = Date.now() + expiresIn * 1000;
		} else if (jwtPayload?.exp) {
			session.expiresAt = jwtPayload.exp * 1000;
		} else {
			session.expiresAt = Date.now() + 15 * 60 * 1000;
		}

		localStorage.setItem(this.KEYS.SESSION, JSON.stringify(session));

		const existingCtMc = JSON.parse(
			localStorage.getItem('ct_mc') || '{"mcid":"","mcsp":"free"}',
		);

		const ct_mc = {
			mcid: session.user.id || '',
			mcsp: subscription?.planName || existingCtMc.mcsp || 'free',
		};
		localStorage.setItem('ct_mc', JSON.stringify(ct_mc));
		this.syncCompatibilityCookies(session.user, ct_mc);
	},

	syncCompatibilityCookies(user = {}, ctMc = null) {
		const userId = user.id || user.userId || '';
		const planName = ctMc?.mcsp || 'free';

		if (!userId) return;

		document.cookie = `iumc=${encodeURIComponent(userId)}; path=/; SameSite=Lax`;
		document.cookie = `mcid=${encodeURIComponent(userId)}; path=/; SameSite=Lax`;
		document.cookie = `mcsp=${encodeURIComponent(planName)}; path=/; SameSite=Lax`;
		document.cookie = `ct_mc=${encodeURIComponent(
			JSON.stringify({ mcid: userId, mcsp: planName }),
		)}; path=/; SameSite=Lax`;
	},

	updateSubscriptionPlan(planName) {
		if (!planName) {
			return;
		}

		const ct_mc = JSON.parse(
			localStorage.getItem('ct_mc') || '{"mcid":"","mcsp":"free"}',
		);
		ct_mc.mcsp = planName;
		localStorage.setItem('ct_mc', JSON.stringify(ct_mc));
		this.syncCompatibilityCookies(this.getSession()?.user || {}, ct_mc);
	},

	getSession() {
		const sessionStr = localStorage.getItem(this.KEYS.SESSION);
		if (!sessionStr) return null;

		try {
			return JSON.parse(sessionStr);
		} catch (error) {
			console.error('Erro ao parsear sessão:', error);
			return null;
		}
	},

	getAccessToken() {
		const session = this.getSession();
		return session?.accessToken || null;
	},

	updateAccessToken(accessToken, expiresIn) {
		const session = this.getSession() || { user: {} };
		session.user = session.user || {};
		session.accessToken = accessToken;

		if (expiresIn) {
			session.expiresAt = Date.now() + expiresIn * 1000;
		} else {
			const payload = this.decodeJWT(accessToken);
			if (payload?.exp) {
				session.expiresAt = payload.exp * 1000;
			}
		}

		localStorage.setItem(this.KEYS.SESSION, JSON.stringify(session));
	},

	updateUserData(userData) {
		const session = this.getSession() || { user: {} };
		session.user = session.user || {};
		const tokenPayload = this.decodeJWT(session.accessToken);
		const tokenIdentity = tokenPayload
			? {
					userId: tokenPayload.userId || tokenPayload.sub,
					email: tokenPayload.email,
				}
			: null;

		if (tokenIdentity && !this.identitiesMatch(tokenIdentity, userData)) {
			this.clearSession();
			return false;
		}

		if (userData.userId !== undefined && userData.userId !== null) {
			session.user.id = session.user.id || userData.userId;
		}
		if (userData.orderId !== undefined && userData.orderId !== null) {
			session.user.orderId = userData.orderId;
		}
		if (userData.name) {
			session.user.name = userData.name;
		}
		if (userData.email) {
			session.user.email = session.user.email || userData.email;
		}
		if (userData.image) {
			session.user.image = userData.image;
		}

		localStorage.setItem(this.KEYS.SESSION, JSON.stringify(session));

		console.log('User data updated:', userData);

		if (userData.userId || userData.subscription) {
			const ct_mc = JSON.parse(
				localStorage.getItem('ct_mc') || '{"mcid":"","mcsp":"free"}',
			);
			if (userData.userId) {
				ct_mc.mcid = session.user.id || userData.userId;
			}
			if (
				userData.subscription &&
				(userData.subscription.planName || userData.subscription.plan)
			) {
				ct_mc.mcsp =
					userData.subscription.planName || userData.subscription.plan;
			}
			localStorage.setItem('ct_mc', JSON.stringify(ct_mc));
			this.syncCompatibilityCookies(session.user, ct_mc);
		}

		return true;
	},

	updateUserDataFromProfile(profileData) {
		this.updateUserData({
			name: profileData.name || profileData.fullName,
			email: profileData.email,
			image: profileData.image || profileData.avatar,
			userId: profileData.userId || profileData.id,
			orderId: profileData.orderId,
		});
	},

	getUserId() {
		const session = this.getSession();
		return session?.user?.id || null;
	},

	getOrderId() {
		const session = this.getSession();
		return session?.user?.orderId || null;
	},

	getUserData() {
		const session = this.getSession();
		if (!session?.user) return null;

		const { id, orderId, name, email, image } = session.user;
		if (!name && !email && !id) return null;

		return { userId: id, orderId, name, email, image };
	},

	isAuthenticated() {
		return !!this.getAccessToken();
	},

	clearSession() {
		localStorage.removeItem(this.KEYS.SESSION);
		localStorage.removeItem('climatempo_last_refresh');
		localStorage.removeItem(this.KEYS.LAST_SUBSCRIPTION_SYNC);
		localStorage.removeItem('ct_mc');
		document.cookie = 'iumc=; Max-Age=0; path=/; SameSite=Lax';
		document.cookie = 'mcid=; Max-Age=0; path=/; SameSite=Lax';
		document.cookie = 'mcsp=; Max-Age=0; path=/; SameSite=Lax';
		document.cookie = 'ct_mc=; Max-Age=0; path=/; SameSite=Lax';
	},

	async refreshAccessToken(apiClient) {
		try {
			const currentAccessToken = this.getAccessToken();
			const config = currentAccessToken
				? {
						headers: {
							Authorization: `Bearer ${currentAccessToken}`,
						},
					}
				: {};

			const response = await apiClient.axiosInstance.post(
				appLocalUrl('/v2/json/auth/refresh'),
				{},
				config,
			);

			const { accessToken } = response.data;

			if (accessToken) {
				const session = this.getSession() || { user: {} };
				session.accessToken = accessToken;

				const payload = this.decodeJWT(accessToken);
				if (payload?.exp) {
					session.expiresAt = payload.exp * 1000;
				} else {
					session.expiresAt = Date.now() + 15 * 60 * 1000;
				}

				localStorage.setItem(this.KEYS.SESSION, JSON.stringify(session));
				return accessToken;
			}

			this.clearSession();
		} catch (error) {
			console.error('Erro ao fazer refresh do token:', error);

			if (error.response?.status === 401 || error.response?.status === 403) {
				this.clearSession();
				localStorage.removeItem('climatempo_last_refresh');
			}

			return null;
		}
	},
};

export default SessionManager;
